import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import Modal from '../../components/common/Modal';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { USER_ROLES, RESOURCE_TYPES } from '../../utils/constants';
import permissionService from '../../api/services/permissionService';
import { handleApiError } from '../../utils/helpers';
import GrantPermissionModal from '../../components/admin/permissions/GrantPermissionModal';

const PermissionsPage = () => {
  const { user } = useAuth();
  const { success, error } = useToast();
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [showGrantModal, setShowGrantModal] = useState(false);

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const response = await permissionService.getGrantedPermissions();
      setPermissions(response.data || []);
    } catch (err) {
      error(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRevokePermission = async (permissionId) => {
    if (!window.confirm('Are you sure you want to revoke this permission?')) {
      return;
    }

    try {
      await permissionService.revokePermission(permissionId);
      success('Permission revoked successfully');
      fetchPermissions();
    } catch (err) {
      error(handleApiError(err));
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Manage Permissions">
        <Loader size="lg" />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Manage Permissions"
      subtitle="Grant and manage permissions for Sub-Admins"
      actions={
        <Button onClick={() => setShowGrantModal(true)}>
          Grant Permission
        </Button>
      }
    >
      {/* Permissions List */}
      <Card>
        {permissions.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No permissions granted yet
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by granting a permission to a Sub-Admin
            </p>
            <div className="mt-6">
              <Button onClick={() => setShowGrantModal(true)}>
                Grant Permission
              </Button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resource Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Permissions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Granted On
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {permissions.map((permission) => (
                  <tr key={permission.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-primary-600 font-medium text-sm">
                              {permission.user?.firstName?.[0]}
                              {permission.user?.lastName?.[0]}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {permission.user?.firstName} {permission.user?.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {permission.user?.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {permission.resourceType}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {permission.canView && (
                          <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800">
                            View
                          </span>
                        )}
                        {permission.canCreate && (
                          <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                            Create
                          </span>
                        )}
                        {permission.canEdit && (
                          <span className="px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-800">
                            Edit
                          </span>
                        )}
                        {permission.canDelete && (
                          <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800">
                            Delete
                          </span>
                        )}
                        {permission.canManageAll && (
                          <span className="px-2 py-1 text-xs rounded bg-purple-100 text-purple-800">
                            Manage All
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(permission.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleRevokePermission(permission.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Grant Permission Modal */}
      {showGrantModal && (
        <GrantPermissionModal
          onClose={() => setShowGrantModal(false)}
          onSuccess={() => {
            setShowGrantModal(false);
            fetchPermissions();
          }}
        />
      )}
    </DashboardLayout>
  );
};

export default PermissionsPage;